import api from '../../services/api';
import { ResponseData } from '../../types/response/ResponseData';
import ResponseError from '../../types/response/ResponseError';
import { CreateOrganizationRequest, Organization } from '../../types/Organization';

export const listOrganizations = async (
    getOrganizationsSucceeded: (response: Organization[]) => void,
    getOrganizationsFailed: (error: ResponseError) => void
) => {
    try {
        const response = await api.get<ResponseData<Organization[]>>('organizations');
        getOrganizationsSucceeded(response.data);
    } catch (error) {
        getOrganizationsFailed(error as ResponseError);
    }
};

export const enableOrganization = async (
    id: string,
    enableOrganizationStarted: () => void,
    enableOrganizationSucceeded: () => void,
    enableOrganizationFailed: (error: ResponseError) => void
) => {
    enableOrganizationStarted();

    try {
        await api.post(`organizations/${id}/features/checkout/enable`, {});

        enableOrganizationSucceeded();
    } catch (error) {
        enableOrganizationFailed(error as ResponseError);
    }
};

export const createOrganization = async (
    request: CreateOrganizationRequest,
    createOrganizationStarted: () => void,
    createOrganizationSucceeded: (organizationName: string) => void,
    createOrganizationFailed: (error: ResponseError) => void,
    addMerchantFailed: (error: ResponseError) => void,
    inviteAdministratorFailed: (error: ResponseError) => void,
    getOrganizationsSucceeded: (response: Organization[]) => void,
    getOrganizationsFailed: (error: ResponseError) => void
) => {
    createOrganizationStarted();

    let organizationId: string;
    try {
        const response = await api.post('organizations', { name: request.name });
        organizationId = response.entityId;
    } catch (error) {
        createOrganizationFailed(error as ResponseError);
        return;
    }

    let hasErrors = false;

    try {
        // The backend resolves the merchant in Bits KYC (creating it there with the
        // legal name when missing); company data is fetched later by the merchant sync.
        // Agreements are signed outside Merchant Hub for now, so the merchant is added as already signed.
        await api.post(`organizations/${organizationId}/merchants`, {
            countryCode: request.countryCode,
            organizationNumber: request.organizationNumber,
            name: request.merchantName,
            allowedUrls: request.allowedUrls,
            alreadySigned: true,
        });
    } catch (error) {
        hasErrors = true;
        addMerchantFailed(error as ResponseError);
    }

    if (request.sendInvitation) {
        try {
            // Omitting groups: a new organization has only the default
            // "Administrators" group, so the invitee is auto-assigned to it.
            await api.post(`organizations/${organizationId}/invitations`, {
                email: request.adminEmail,
                name: request.adminEmail,
            });
        } catch (error) {
            hasErrors = true;
            inviteAdministratorFailed(error as ResponseError);
        }
    }

    if (!hasErrors) {
        createOrganizationSucceeded(request.name);
    }

    await listOrganizations(getOrganizationsSucceeded, getOrganizationsFailed);
};

export const deleteOrganization = async (
    organizationId: string,
    organizationName: string,
    deleteOrganizationStarted: () => void,
    deleteOrganizationSucceeded: (organization: string) => void,
    deleteOrganizationFailed: (error: ResponseError) => void
) => {
    deleteOrganizationStarted();

    try {
        await api.del(`organizations/${organizationId}`);

        deleteOrganizationSucceeded(organizationName);
    } catch (error) {
        deleteOrganizationFailed(error as ResponseError);
    }
};
